import axios from "axios";
import store from "@/store";
import {
  applyAuthTokensFromResponse,
  getAccessTokenFromResponse,
  getCookieRefreshConfig,
  getRefreshTokenConfig,
  isCookieRefreshEnabled,
  shouldStoreRefreshToken,
} from "@/utils/auth-tokens";
import { getRefreshTokenFromResponse } from "@/utils/auth-security-policy.mjs";
import { clearRefreshLock, isAnotherTabRefreshing, setRefreshLock, waitForRefreshLockToClear } from "@/utils/tokenRefreshLock";
import { createTokenChannel } from "@/utils/tokenChannel";
import { isLicenseInactiveResponse } from "@/features/auth/license-error-policy.mjs";
import { getAuthIdentity, isCurrentAuthSession, markAuthTransitioning } from "@/utils/auth-storage-sync.mjs";

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-type": "application/json",
  },
});

let refreshPromise = null;
let tokenChannel = null;

const REFRESH_ERROR_REASON = {
  REFRESH_TOKEN_NOT_RECEIVED: "REFRESH_TOKEN_NOT_RECEIVED",
  REFRESH_TOKEN_INVALID: "REFRESH_TOKEN_INVALID",
  REFRESH_TOKEN_EXPIRED: "REFRESH_TOKEN_EXPIRED",
  REFRESH_TOKEN_USER_NOT_FOUND: "REFRESH_TOKEN_USER_NOT_FOUND",
  REFRESH_TOKEN_USER_INACTIVE: "REFRESH_TOKEN_USER_INACTIVE",
  GOOGLE_REFRESH_TOKEN_MISSING: "GOOGLE_REFRESH_TOKEN_MISSING",
  GOOGLE_ACCESS_TOKEN_MISSING: "GOOGLE_ACCESS_TOKEN_MISSING",
  GOOGLE_BACKEND_TOKEN_UPDATE_FAILED: "GOOGLE_BACKEND_TOKEN_UPDATE_FAILED",
  DJANGO_ACCESS_TOKEN_MISSING: "DJANGO_ACCESS_TOKEN_MISSING",
  UNSUPPORTED_AUTH_USER_TYPE: "UNSUPPORTED_AUTH_USER_TYPE",
};

const BE_REFRESH_ERROR_REASON_BY_CODE = {
  1001053: REFRESH_ERROR_REASON.REFRESH_TOKEN_NOT_RECEIVED,
  1001054: REFRESH_ERROR_REASON.REFRESH_TOKEN_INVALID,
  1001061: REFRESH_ERROR_REASON.REFRESH_TOKEN_INVALID,
  1001062: REFRESH_ERROR_REASON.REFRESH_TOKEN_EXPIRED,
  1001063: REFRESH_ERROR_REASON.REFRESH_TOKEN_NOT_RECEIVED,
  1001065: REFRESH_ERROR_REASON.REFRESH_TOKEN_USER_NOT_FOUND,
  1001066: REFRESH_ERROR_REASON.REFRESH_TOKEN_USER_INACTIVE,
  1001079: REFRESH_ERROR_REASON.REFRESH_TOKEN_USER_NOT_FOUND,
};

function createRefreshError(reason, message, cause = null) {
  const error = new Error(message);
  error.reason = reason;
  error.cause = cause;
  return error;
}

function getBackendErrorData(error) {
  return error && error.response && error.response.data ? error.response.data : {};
}

function getBackendRefreshErrorReason(error) {
  const errorData = getBackendErrorData(error);
  const errCode = Number(errorData.err_code);
  return BE_REFRESH_ERROR_REASON_BY_CODE[errCode] || null;
}

function createBackendRefreshError(error) {
  const errorData = getBackendErrorData(error);
  const reason = getBackendRefreshErrorReason(error) || REFRESH_ERROR_REASON.REFRESH_TOKEN_INVALID;
  const message = errorData.err_msg || errorData.detail || "Failed to refresh access token";
  const refreshError = createRefreshError(reason, message, error);
  refreshError.errCode = errorData.err_code;
  refreshError.errMsg = errorData.err_msg;
  return refreshError;
}

function getTokenChannel() {
  if (!tokenChannel) {
    tokenChannel = createTokenChannel((token) => {
      store.commit("setAccessToken", token);
    }, () => store.state);
  }

  return tokenChannel;
}

export function isTokenRefreshRequest(url = "") {
  return /account\/update-token(?:-recolumn)?\/?/.test(url);
}

export function isAuthRequest(url = "") {
  return /account\/(?:login|google-oauth|sign-up[^/]*)\/?/.test(url);
}

export function isLicenseActiveError(error) {
  return isLicenseInactiveResponse(error && error.response);
}

export function isGoogleLoginEnabled() {
  return process.env.VUE_APP_ENABLE_GOOGLE_LOGIN !== "false";
}

function getUserType() {
  return store.state.user_info && store.state.user_info.user_type;
}

function commitTokens(data = {}) {
  applyAuthTokensFromResponse(store, data);
  const refreshToken = getRefreshTokenFromResponse(data);
  if (refreshToken && shouldStoreRefreshToken(store.state.auth_refresh_mode)) {
    store.commit("setRefreshToken", refreshToken);
  }
}

export async function loginWithPassword({ email, password }) {
  const response = await apiClient.post("account/login/", { email, password });
  commitTokens(response.data);

  const accessToken = getAccessTokenFromResponse(response.data);
  if (!accessToken) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.DJANGO_ACCESS_TOKEN_MISSING,
      "Django access token missing from login response"
    );
  }

  return accessToken;
}

export async function logoutDjangoSession() {
  const userType = getUserType();
  const shouldLogoutServerSession =
    userType === "django" ||
    userType === "django-prosecution" ||
    (isCookieRefreshEnabled(store.state.auth_refresh_mode) && userType !== "google");

  try {
    if (shouldLogoutServerSession) {
      await apiClient.post("account/logout/", null, getCookieRefreshConfig());
    }
  } finally {
    store.commit("logout");
  }
}

function assertRefreshSession(identity) {
  if (!isCurrentAuthSession(store.state, identity, window.localStorage)) {
    // Storage may change before its event is delivered. Freeze this stale state
    // now so legacy methodRecall error handlers cannot persist it or log out B.
    if (identity && getAuthIdentity(store.state) === identity) {
      markAuthTransitioning(store.state);
    }
    const error = new Error("Authentication changed while refreshing the token");
    error.code = "AUTH_SESSION_CHANGED";
    throw error;
  }
}

async function refreshGoogleToken(identity) {
  if (!store.state.refresh_token) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.GOOGLE_REFRESH_TOKEN_MISSING,
      "Google refresh token is missing"
    );
  }

  const googleTokenRes = await axios.post("https://oauth2.googleapis.com/token", {
    client_id: process.env.VUE_APP_CLIENT_ID,
    client_secret: process.env.VUE_APP_CLIENT_SECRET,
    refresh_token: store.state.refresh_token,
    grant_type: "refresh_token",
  });

  const newAccessToken = googleTokenRes.data.access_token;
  if (!newAccessToken) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.GOOGLE_ACCESS_TOKEN_MISSING,
      "Google access token missing from refresh response"
    );
  }

  assertRefreshSession(identity);
  store.commit("setAccessToken", newAccessToken);
  if (googleTokenRes.data.id_token) {
    store.commit("setIdToken", googleTokenRes.data.id_token);
  }
  getTokenChannel().postMessage({ type: "NEW_ACCESS_TOKEN", token: newAccessToken, identity });

  try {
    await apiClient.get("account/update-token/", {
      headers: { Authorization: `Bearer ${newAccessToken}` },
    });
  } catch (error) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.GOOGLE_BACKEND_TOKEN_UPDATE_FAILED,
      "Failed to sync refreshed Google access token with backend",
      error
    );
  }

  assertRefreshSession(identity);
  return newAccessToken;
}

async function refreshDjangoToken(identity) {
  const refreshConfig = getRefreshTokenConfig(store.state.refresh_token, store.state.auth_refresh_mode);
  let djangoTokenRes = null;

  try {
    djangoTokenRes = await apiClient.get("account/update-token-recolumn/", refreshConfig);
  } catch (error) {
    throw createBackendRefreshError(error);
  }

  assertRefreshSession(identity);
  commitTokens(djangoTokenRes.data);

  const newAccessToken = getAccessTokenFromResponse(djangoTokenRes.data);
  if (!newAccessToken) {
    throw createRefreshError(
      REFRESH_ERROR_REASON.DJANGO_ACCESS_TOKEN_MISSING,
      "Django access token missing from refresh response"
    );
  }

  getTokenChannel().postMessage({ type: "NEW_ACCESS_TOKEN", token: newAccessToken, identity });
  return newAccessToken;
}

async function runRefresh() {
  const identity = getAuthIdentity(store.state);
  assertRefreshSession(identity);
  store.commit("setIsMethodRecalling", true);
  setRefreshLock();

  try {
    const userType = getUserType();

    if (userType === "google") {
      return await refreshGoogleToken(identity);
    }

    if (userType === "django" || userType === "django-prosecution") {
      return await refreshDjangoToken(identity);
    }

    if (isCookieRefreshEnabled(store.state.auth_refresh_mode) && Object.keys(store.state.user_info || {}).length > 0) {
      return await refreshDjangoToken(identity);
    }

    throw createRefreshError(
      REFRESH_ERROR_REASON.UNSUPPORTED_AUTH_USER_TYPE,
      "Unsupported auth user type"
    );
  } catch (error) {
    // An old request's failure must not log out a newly selected account either.
    assertRefreshSession(identity);
    throw error;
  } finally {
    clearRefreshLock();
    if (isCurrentAuthSession(store.state, identity, window.localStorage)) {
      store.commit("setIsMethodRecalling", false);
    }
  }
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  if (isAnotherTabRefreshing()) {
    const identity = getAuthIdentity(store.state);
    assertRefreshSession(identity);
    await waitForRefreshLockToClear();
    assertRefreshSession(identity);
    if (store.state.access_token) return store.state.access_token;
  }

  refreshPromise = runRefresh()
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export function refreshAccessTokenAsMethodRecall() {
  return refreshAccessToken()
    .then(() => "access token 갱신 및 저장 완료 -> method 재호출")
    .catch(() => Promise.reject(1));
}

export function handleRefreshFailure(error) {
  if (error?.code === "AUTH_SESSION_CHANGED") return;
  store.commit("setOverlay", false);
  store.commit("setOverlayText", "");
  store.commit("logout");
  import("@/router").then(({ default: router }) => {
    router.push("/").catch(() => {});
  });
}
